import React, { useState, useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  DNABox,
  DNAButton,
  DatePicker,
  DNAText,
  Spinner,
} from '@alucio/lux-ui'
import { CommonIcon } from 'src/components/DNA'
import logger from 'src/utils/logger'
import { startAthenaQueryExecutionMeetingReport } from '@alucio/aws-beacon-amplify/src/graphql/mutations'
import { generateClient } from 'aws-amplify/api'
import { useCurrentUser } from 'src/state/redux/selector/user'
import { onCsvExportReady } from '@alucio/aws-beacon-amplify/src/graphql/subscriptions'
import { format } from 'date-fns'

type DateRangeForm = {
  startDate: Date | undefined
  endDate: Date | undefined
}

enum DownloadState {
  Idle = 'Idle',
  Downloading = 'Downloading',
  Error = 'Error',
  Done = 'Done',
}

const formatDate = (date: Date | undefined) => {
  if (!date) return ''
  return format(date, 'yyyy-MM-dd')
}

const ExportCsvReport: React.FC = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<DateRangeForm>({
    mode: 'onChange',
    defaultValues: {
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      endDate: new Date(),
    },
  })

  const currentUser = useCurrentUser()
  const currentUserId = currentUser.userProfile?.id
  const latestSubmittedDatesRef = useRef<{ start?: Date; end?: Date }>({})
  const queryExecutionIdRef = useRef<string | null>(null)
  const { startDate, endDate } = watch()
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const [downloadState, setDownloadState] = useState<DownloadState>(DownloadState.Idle)
  const [fileUrl, setFileUrl] = useState<string | null>(null)

  const downloadCsvFromUrl = async (signedUrl: string) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 180 * 1000) // 3 minutes

    try {
      const { start, end } = latestSubmittedDatesRef.current
      const response = await fetch(signedUrl, { signal: controller.signal })
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `User Audit Information-${start ? formatDate(start) : ''}-${end ? formatDate(end) : ''}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      setDownloadState(DownloadState.Done)
      setFileUrl(null)
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        logger.report.error('CSV download timed out');
      } else {
        logger.report.error('Error downloading CSV:', error)
      }
      setDownloadState(DownloadState.Error)
    }
  }

  useEffect(() => {
    const appsyncClient = generateClient()
    if (!currentUserId) return

    const subscriptionObservable = appsyncClient.graphql({
      query: onCsvExportReady,
      variables: { userId: currentUserId },
    })

    const subscription = subscriptionObservable.subscribe({
      next: async ({ data }) => {
        const notification = data?.onCsvExportReady
        if (notification?.signedUrl && notification?.queryExecutionId === queryExecutionIdRef.current) {
          setFileUrl(notification.signedUrl)
          await downloadCsvFromUrl(notification.signedUrl)
        } else {
          logger.email.warn('Missing signedUrl in CSV export subscription:', notification)
          setDownloadState(DownloadState.Error)
        }
      },
      error: (err) => {
        logger.report.error('Subscription error', JSON.stringify(err, Object.getOwnPropertyNames(err)))
        setDownloadState(DownloadState.Error)
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [currentUserId])

  const startReportGeneration = async (startDate?: Date, endDate?: Date) => {
    if (!startDate || !endDate) return
    latestSubmittedDatesRef.current = { start: startDate, end: endDate }
    try {
      setDownloadState(DownloadState.Downloading)
      const appsyncClient = generateClient()
      const { data } = await appsyncClient.graphql({
        query: startAthenaQueryExecutionMeetingReport,
        variables: { startDate: formatDate(startDate), endDate: formatDate(endDate) },
      })
      queryExecutionIdRef.current = data.startAthenaQueryExecutionMeetingReport
    } catch (error) {
      logger.email.error('Failed to start CSV report generation:', error)
      setDownloadState(DownloadState.Error)
    }
  }

  const onSubmit = (data: DateRangeForm) => {
    startReportGeneration(data.startDate, data.endDate)
  }

  if (downloadState === DownloadState.Downloading) {
    return (
      <DNABox appearance="col" alignX="center" alignY="center" fill style={{ minHeight: 400 }}>
        <Spinner size="lg" />
        <DNAText status="secondary" style={{ marginTop: 5 }}>
          Your download should start automatically.
          {fileUrl ? (
            <React.Fragment>
              {' If it does not, please click '}
              <a href={fileUrl} onClick={() => setDownloadState(DownloadState.Idle)}>this link</a>.
              {'.'}
            </React.Fragment>
          ) : ''}
        </DNAText>
      </DNABox>
    )
  }

  if (downloadState === DownloadState.Error) {
    return (
      <DNABox appearance="col" alignX="center" alignY="center" fill spacing="sm" style={{ minHeight: 400 }}>
        <CommonIcon variant="warning" status="danger" size="xl" />
        <DNAText bold>
          Sorry, the download failed. Please try again.
        </DNAText>
        <DNAButton
          appearance="outline"
          status="tertiary"
          onPress={() => {
            const { start, end } = latestSubmittedDatesRef.current
            if (start && end) {
              setDownloadState(DownloadState.Downloading)
              startReportGeneration(start, end)
            }
          }}
        >
          Try again
        </DNAButton>
      </DNABox>
    )
  }

  function getSixMonthsAgo(): Date {
    const today = new Date();
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    sixMonthsAgo.setHours(0, 0, 0, 0);
    return sixMonthsAgo;
  }

  return (
    <DNABox appearance="col" spacing="lg" style={{ marginHorizontal: 10, minWidth: 500 }}>
      {/* Start Date */}
      <DNABox appearance="col">
        <DNABox appearance="row" style={{ marginBottom: 4 }}>
          <DNAText bold style={{ color: 'red' }}>* </DNAText>
          <DNAText bold>Start Date</DNAText>
        </DNABox>
        <Controller
          name="startDate"
          control={control}
          rules={{
            required: 'Start date is required',
            validate: (value) => {
              if (!value) return 'Start date is required';
              if (value > today) return 'Start date cannot be in the future';
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <>
              <DatePicker selectedDate={value} onDateSelected={onChange} minDate={getSixMonthsAgo()} maxDate={today} />
              {errors.startDate && (
                <DNAText status="danger">{errors.startDate.message}</DNAText>
              )}
            </>
          )}
        />
      </DNABox>

      {/* End Date */}
      <DNABox appearance="col">
        <DNABox appearance="row" style={{ marginBottom: 4 }}>
          <DNAText bold style={{ color: 'red' }}>* </DNAText>
          <DNAText bold>End Date</DNAText>
        </DNABox>
        <Controller
          name="endDate"
          control={control}
          rules={{
            required: 'End date is required',
            validate: (value) => {
              if (!value) return 'End date is required';
              if (value > today) return 'End date cannot be in the future';
              if (startDate && value < startDate) return 'End date cannot be before start date';
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <>
              <DatePicker selectedDate={value} onDateSelected={onChange} maxDate={today} />
              {errors.endDate && (
                <DNAText status="danger">{errors.endDate.message}</DNAText>
              )}
            </>
          )}
        />
      </DNABox>

      <DNAButton
        appearance="outline"
        status="tertiary"
        disabled={!startDate || !endDate}
        onPress={handleSubmit(onSubmit)}
      >
        Download all user information
      </DNAButton>
    </DNABox>
  )
}

export default ExportCsvReport
